import type { Schemas } from '@kanos/api-client';
import { EXPORT_WINDOW_DAYS, LOCKED_DAYS } from '@kanos/domain';
import {
  Button,
  DateField,
  ModalDialog,
  StatusBadge,
  TextField,
  type Tone,
  useToast,
} from '@kanos/ui';
import {
  api,
  Card,
  closedBy,
  failure,
  formatDate,
  ProblemAlert,
  QueryContent,
} from '@kanos/web-core';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

type PlatformFirm = Schemas['PlatformFirm'];
type ContractPhase = Schemas['ContractPhase'];

const firmsKey = ['platform', 'firms'] as const;

const firmsQuery = queryOptions({
  queryKey: firmsKey,
  queryFn: async (): Promise<PlatformFirm[]> => {
    const { data, error, response } = await api.GET('/api/v1/platform/firms');
    if (!data) throw failure('listPlatformFirms', error, response.status);
    return data;
  },
});

const phaseBadge: Record<ContractPhase, { tone: Tone; label: string }> = {
  active: { tone: 'success', label: 'Aktiv' },
  'export-window': { tone: 'warning', label: 'Exportfenster' },
  locked: { tone: 'danger', label: 'Gesperrt' },
  'erasure-due': { tone: 'danger', label: 'Löschung fällig' },
};

const sourceLabel = { stripe: 'aus Stripe', platform: 'vom Kanos-Team' } as const;

function useSetEnd(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (endsOn: string | null) => {
      const { data, error, response } = await api.PUT(
        '/api/v1/platform/firms/{tenantId}/contract-end',
        { params: { path: { tenantId } }, body: { endsOn } },
      );
      if (!data) throw failure('setFirmContractEnd', error, response.status);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: firmsKey }),
  });
}

/**
 * The release of a firm's erasure (ADR-045 §5): its name typed as a second look, with a second
 * factor. Not to be undone - the housekeeping erases the firm with its next run.
 */
function ReleaseDialog({ firm, onClose }: { firm: PlatformFirm; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const release = useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST(
        '/api/v1/platform/firms/{tenantId}/erasure-release',
        { params: { path: { tenantId: firm.id } }, body: { name } },
      );
      if (!response.ok) throw failure('releaseFirmErasure', error, response.status);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: firmsKey });
      toast({ tone: 'success', title: 'Löschung freigegeben' });
      onClose();
    },
  });
  return (
    <ModalDialog
      title={`Löschung freigeben – ${firm.name}`}
      description="Exportfenster und Sperre sind vorbei. Nach der Freigabe löscht Kanos alle Daten der Kanzlei samt Sandboxes; das lässt sich nicht rückgängig machen. Organisation und Schlüssel entfernen Sie danach über die Aufgaben."
      isOpen
      onOpenChange={closedBy(onClose)}
      footer={(close) => (
        <>
          <Button variant="quiet" onPress={close}>
            Abbrechen
          </Button>
          <Button
            variant="danger"
            isDisabled={name.trim().length === 0}
            isPending={release.isPending}
            onPress={() => release.mutate()}
          >
            Löschung freigeben
          </Button>
        </>
      )}
    >
      {() => (
        <div className="grid gap-4">
          <TextField
            label="Name der Kanzlei"
            description="Zur Bestätigung genau so, wie er oben steht."
            placeholder={`z. B. ${firm.name}`}
            value={name}
            onChange={setName}
          />
          {release.error ? <ProblemAlert error={release.error} /> : null}
        </div>
      )}
    </ModalDialog>
  );
}

/** Set, move or take back a firm's contract end - with a second factor (ADR-045). */
function ContractEndDialog({ firm, onClose }: { firm: PlatformFirm; onClose: () => void }) {
  const toast = useToast();
  const save = useSetEnd(firm.id);
  const [endsOn, setEndsOn] = useState<string | null>(firm.contract.endsOn);
  const set = firm.contract.endsOn !== null;
  return (
    <ModalDialog
      title={`Vertragsende – ${firm.name}`}
      description={`Der letzte Tag der vollen Nutzung. Danach ${String(EXPORT_WINDOW_DAYS)} Tage nur lesen und exportieren, dann ${String(LOCKED_DAYS)} Tage gesperrt; erst dann geben Sie die Löschung frei. Stripe überschreibt ein hier gesetztes Ende nicht.`}
      isOpen
      onOpenChange={closedBy(onClose)}
      footer={(close) => (
        <>
          {set && (
            <Button
              variant="quiet"
              isPending={save.isPending && save.variables === null}
              onPress={() =>
                save.mutate(null, {
                  onSuccess: () => {
                    toast({ tone: 'success', title: 'Vertragsende zurückgenommen' });
                    onClose();
                  },
                })
              }
            >
              Zurücknehmen
            </Button>
          )}
          <Button variant="quiet" onPress={close}>
            Abbrechen
          </Button>
          <Button
            isDisabled={endsOn === null}
            isPending={save.isPending && save.variables !== null}
            onPress={() =>
              save.mutate(endsOn, {
                onSuccess: () => {
                  toast({ tone: 'success', title: 'Vertragsende gespeichert' });
                  onClose();
                },
              })
            }
          >
            Speichern
          </Button>
        </>
      )}
    >
      {() => (
        <div className="grid gap-4">
          <DateField
            label="Letzter Vertragstag"
            description="Nie vor ein bisher gesetztes Ende in die Vergangenheit."
            value={endsOn}
            onChange={setEndsOn}
            isRequired
          />
          {save.error ? <ProblemAlert error={save.error} /> : null}
        </div>
      )}
    </ModalDialog>
  );
}

function FirmItem({ firm }: { firm: PlatformFirm }) {
  const [editing, setEditing] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const { contract } = firm;
  const { tone, label } = phaseBadge[contract.phase];
  return (
    <li className="glass grid gap-2 rounded-lg p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">{firm.name}</span>
        <StatusBadge tone={tone}>{label}</StatusBadge>
      </div>
      <p className="text-sm text-muted">
        {contract.endsOn && contract.source
          ? `Vertragsende ${formatDate(contract.endsOn)} (${sourceLabel[contract.source]}) · Export bis ${formatDate(contract.exportUntil ?? contract.endsOn)} · gesperrt bis ${formatDate(contract.lockedUntil ?? contract.endsOn)}`
          : `Kein Vertragsende · seit ${formatDate(firm.createdAt)}`}
      </p>
      {firm.erasureReleasedAt ? (
        <p className="text-sm font-semibold">
          Löschung freigegeben am {formatDate(firm.erasureReleasedAt)} – wird mit dem nächsten Lauf
          gelöscht.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onPress={() => setEditing(true)}>
            {contract.endsOn ? 'Vertragsende ändern' : 'Vertragsende setzen'}
          </Button>
          {contract.phase === 'erasure-due' && (
            <Button variant="danger" size="sm" onPress={() => setReleasing(true)}>
              Löschung freigeben
            </Button>
          )}
        </div>
      )}
      {editing && <ContractEndDialog firm={firm} onClose={() => setEditing(false)} />}
      {releasing && <ReleaseDialog firm={firm} onClose={() => setReleasing(false)} />}
    </li>
  );
}

/**
 * The firms of Kanos and where their contracts stand (ADR-045): an end comes from Stripe once the
 * subscription is cancelled, or is set here by the Kanos team - with a second factor, audited in
 * the firm's chain.
 */
export function FirmsPage() {
  const firms = useQuery(firmsQuery);
  return (
    <Card
      id="platform-firms"
      title="Kanzleien"
      description="Endet das Abo einer Kanzlei in Stripe, steht ihr Vertragsende hier von selbst. Sie können es setzen, verschieben oder zurücknehmen – die Admins der Kanzlei werden erinnert."
    >
      <QueryContent query={firms}>
        {(list) =>
          list.length === 0 ? (
            <p className="text-sm text-muted">Noch keine Kanzleien.</p>
          ) : (
            <ul className="grid gap-2">
              {list.map((firm) => (
                <FirmItem key={firm.id} firm={firm} />
              ))}
            </ul>
          )
        }
      </QueryContent>
    </Card>
  );
}
